/**
 * App — composant racine avec routing public + privé.
 */
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";

import { AuthProvider } from "./contexts/AuthContext";
import { MembersProvider } from "./contexts/MembersContext";
import { ToastProvider } from "./contexts/ToastContext";

import Layout from "./components/Layout";
import ErrorBoundary from "./components/ErrorBoundary";

// Public
import LandingPage from "./pages/LandingPage";
import Login from "./pages/Login";
import Inscription from "./pages/Inscription";
import OnboardingEglise from "./pages/OnboardingEglise";
import PagePubliqueEglise from "./pages/PagePubliqueEglise";
import Confidentialite from "./pages/Confidentialite";

// Privé
import Dashboard from "./pages/Dashboard";
import Annuaire from "./pages/Annuaire";
import Evenements from "./pages/Evenements";
import Finances from "./pages/Finances";
import Parametres from "./pages/Parametres";
import Profil from "./pages/Profil";
import Presences from "./pages/Presences";
import Cellules from "./pages/Cellules";
import Benevoles from "./pages/Benevoles";

import useAuth from "./hooks/useAuth";

/** Tracking automatique des changements de route vers Google Analytics 4. */
function Analytics() {
  const location = useLocation();
  useEffect(() => {
    if (typeof window !== "undefined" && typeof window.gtag === "function") {
      window.gtag("config", "G-T29Q9N2L56", {
        page_path: location.pathname + location.search,
      });
    }
  }, [location]);
  return null;
}

/** Route protégée par rôle (admin requis). */
function AdminOnly({ children }) {
  const { isAdmin, loading } = useAuth();
  if (loading) return null;
  return isAdmin ? children : <Navigate to="/profil" replace />;
}

/** Gate d'onboarding : redirige vers /onboarding si user connecté sans église. */
function OnboardingGate({ children }) {
  const { user, isAuthenticated, loading } = useAuth();
  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/connexion" replace />;
  if (!user?.eglise_id || !user?.onboarding_complete) {
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

/** Page d'accueil publique : LandingPage si non connecté, redirect Dashboard sinon. */
function Home() {
  const { user, isAdmin, isAuthenticated, loading } = useAuth();
  if (loading) return null;
  if (!isAuthenticated) return <LandingPage />;
  if (!user?.eglise_id || !user?.onboarding_complete) {
    return <Navigate to="/onboarding" replace />;
  }
  return <Navigate to={isAdmin ? "/dashboard" : "/profil"} replace />;
}

export default function App() {
  return (
    <BrowserRouter>
      <Analytics />
      <ToastProvider>
        <AuthProvider>
          <MembersProvider>
            <ErrorBoundary>
              <Routes>
                {/* Page d'accueil publique */}
                <Route path="/" element={<ErrorBoundary><Home /></ErrorBoundary>} />

                {/* Auth publique (acceptent /login en alias rétrocompat) */}
                <Route path="/connexion" element={<ErrorBoundary><Login /></ErrorBoundary>} />
                <Route path="/login" element={<Navigate to="/connexion" replace />} />
                <Route path="/inscription" element={<ErrorBoundary><Inscription /></ErrorBoundary>} />

                {/* Onboarding (connecté sans église) */}
                <Route path="/onboarding" element={<ErrorBoundary><OnboardingEglise /></ErrorBoundary>} />

                {/* Page publique d'une église — accessible sans login */}
                <Route path="/eglise/:slug" element={<ErrorBoundary><PagePubliqueEglise /></ErrorBoundary>} />

                {/* Politique de confidentialité (RGPD) */}
                <Route path="/confidentialite" element={<ErrorBoundary><Confidentialite /></ErrorBoundary>} />

                {/* Routes authentifiées avec sidebar */}
                <Route element={<OnboardingGate><Layout /></OnboardingGate>}>
                  <Route path="/dashboard"  element={<AdminOnly><ErrorBoundary><Dashboard /></ErrorBoundary></AdminOnly>} />
                  <Route path="/annuaire"   element={<AdminOnly><ErrorBoundary><Annuaire /></ErrorBoundary></AdminOnly>} />
                  <Route path="/cellules"   element={<AdminOnly><ErrorBoundary><Cellules /></ErrorBoundary></AdminOnly>} />
                  <Route path="/benevoles"  element={<AdminOnly><ErrorBoundary><Benevoles /></ErrorBoundary></AdminOnly>} />
                  <Route path="/presences"  element={<AdminOnly><ErrorBoundary><Presences /></ErrorBoundary></AdminOnly>} />
                  <Route path="/evenements" element={<ErrorBoundary><Evenements /></ErrorBoundary>} />
                  <Route path="/finances"   element={<ErrorBoundary><Finances /></ErrorBoundary>} />
                  <Route path="/parametres" element={<ErrorBoundary><Parametres /></ErrorBoundary>} />
                  <Route path="/profil"     element={<ErrorBoundary><Profil /></ErrorBoundary>} />
                </Route>

                <Route path="*" element={<Navigate to="/" replace />} />
              </Routes>
            </ErrorBoundary>
          </MembersProvider>
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}
